// ⚠️ BANCO DE PRUEBAS — VARIANTE v2 DEL MODO MODELO. TEMPORAL.
//
// Copia congelada de `components/PickProductPanel.tsx`, el panel de productos que
// comparten TODOS los modos. Existe para poder rediseñarlo en la variante v2 sin
// tocar el de producción: `catalogo-ia`, `design-auto` y `video-influencer` montan el
// original y no se enteran de lo que pase acá.
//
// Ruta de esta variante: `/interfaz-v2/modo/modelo-v2`. Sólo lo monta
// `PresetDetailSheetModelo`.
//
// Mismo criterio que el shell y el panel del paso 2
// (ADR-2026-08-21-modelo-shell-por-variante). Se borra al cerrar el rediseño; si esta
// variante gana, los cambios se promueven al original.

// Grid inline de productos del usuario — reemplaza al sheet separado
// que había antes. Vive dentro del mismo panel lateral / bottom sheet del
// preset, así no hay un modal encima de otro modal.
//
// Tiene dos variantes:
//   • mobile  → iOS-style bottom sheet: drag handle pill + header con
//               "Cancelar" a la izquierda y título "Mis productos" al
//               centro (mismo patrón que los menús de PruebaNuevaInterfaz
//               y EditarImagen). Toma el panel completo sin padding
//               externo.
//   • desktop → versión simple inline: "Volver" link arriba + título +
//               grid. Vive dentro de la options card del split horizontal.
//
// Si el preset trae `tiposProductoSugeridos` y el rubro es de moda,
// separamos en sección "Sugeridos" (matchean model_view) y "Otros".

import { SkuDelProducto } from '../../../components/SkuDelProducto';
import { useIndiceDeBusqueda } from '../../../lib/indiceDeBusquedaDeProductos';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronLeft,
  PackageX,
  PanelLeftOpen,
  Pencil,
  Sparkles,
  Plus,
} from 'lucide-react';
import { AbanicoDeFotos } from '../../../components/AbanicoDeFotos';
import { TarjetaDeSubidaEnGrilla } from '../../../components/TarjetaDeSubidaEnGrilla';
import {
  useUserProducts,
  useUserProductsLoaded,
  useProductEdit,
} from '../productsContext';
import { getLastUsedProductIds } from '../lastUsedProducts';
import {
  applyProductCatalogFilter,
  DEFAULT_PRODUCT_SORT,
  MIN_PRODUCTS_FOR_TOOLBAR,
  type ProductCatalogSort,
} from '../productCatalogFilter';
import {
  ProductCatalogNoResults,
  ProductCatalogToolbar,
} from '../components/ProductCatalogToolbar';
import CachedImg from '../../../components/CachedImg';
import ProductGridSkeleton from '../../../components/ProductGridSkeleton';
import { EditProductFlow } from '../../../components/EditProductFlow';
import type { MockProductV2, PresetV2 } from '../mockData';
// El modo Modelo cuenta sus tres variantes del banco de pruebas (temporal) — si no,
// la bajada de "Subir nuevo producto" les saldría la genérica y no la del modo.
import { esDesignModelo } from '../modesCatalog';
import { palabraDeModeloActual } from '../../../lib/nombreDeModelo';

export interface PickProductPanelModeloProps {
  variant: 'mobile' | 'desktop';
  /** El user confirma su selección (uno o varios productos). */
  onConfirm: (products: MockProductV2[]) => void;
  onBack: () => void;
  /** Si el template tiene tipos sugeridos y el rubro califica, separamos
   *  los productos del user en "Sugeridos" (matchean model_view) y
   *  "Otros productos". */
  tiposProductoSugeridos?: string[];
  rubro?: string | null;
  /** Selección inicial (ids, en orden — el primero es el "principal").
   *  Permite reabrir el picker con lo que ya estaba elegido. */
  initialSelectedIds?: string[];
  /** Si true, el primer seleccionado se marca como "Principal" en su tile
   *  (usado al combinar productos en catálogo). */
  showPrimary?: boolean;
  /** Oculta el link "Volver" del header desktop (cuando el sheet ya tiene
   *  su propio cierre, p.ej. una X). */
  hideBack?: boolean;
  /** Selección ÚNICA: tocar un producto reemplaza al elegido (tocar el mismo
   *  lo deselecciona). Lo usa el modo video-influencer, que genera con un
   *  solo producto (ADR-563). */
  singleSelect?: boolean;
  /**
   * TOPE de productos elegidos. Llegado al tope, las tarjetas NO elegidas se atenúan y
   * dejan de responder al tap: quitar una es lo que habilita sumar otra.
   *
   * Lo pide "Ambiente completo", que mete 2-4 productos en UNA sola foto
   * (ADR-2026-09-03-modo-ambiente-completo): pasado ese número la escena deja de ser un
   * ambiente y pasa a ser un depósito. Sin esto el tope tendría que vivir en el host, que
   * no puede impedir la selección —el panel se la guarda adentro— y sólo podría avisar
   * después. No aplica con `singleSelect`, que ya tiene su propio tope de uno.
   */
  maxSeleccion?: number;
  /** El header (título + subtítulo) va DENTRO del contenedor scrolleable en vez
   *  de fijo arriba, así se va al scrollear y deja el buscador contra el borde.
   *  Lo usa la pantalla full-screen de mobile, donde el alto es poco y tener el
   *  header clavado se come media pantalla. En desktop se deja fijo. */
  scrollHeader?: boolean;
  /** Oculta TODO el header (título + descripción + "Volver"), dejando solo el
   *  buscador y la grilla. Lo usa el sheet de referencias del asistente, que
   *  ya tiene su propio encabezado con pestañas arriba: repetir un título ahí
   *  adentro sumaba ruido y comía alto de pantalla. */
  hideHeader?: boolean;
  /** Oculta el footer con "Confirmar". Lo usa el sheet de referencias del
   *  asistente, que tiene UN solo botón "Utilizar" para todas sus pestañas:
   *  con el footer propio del panel habría dos CTA compitiendo. Requiere
   *  `onSelectionChange` para enterarse de lo elegido. */
  hideFooter?: boolean;
  /** Reporta la selección EN VIVO (no al confirmar). Necesario cuando el
   *  caller oculta el footer y confirma por su cuenta. */
  onSelectionChange?: (products: MockProductV2[]) => void;
  /** Título del header desktop. Default "Elegí los productos". */
  title?: string;
  /** Subtítulo / explicación de qué se está seleccionando. */
  description?: string;
  /** SOLO DESKTOP. Si se provee, el panel muestra ARRIBA del grid (dentro
   *  del scroll) el header "Elegí el producto para generar" + botón "Subir
   *  nuevo producto", fusionando el paso choose-product con la lista. Usado
   *  en los modos de diseño en desktop. En mobile NO se pasa (queda el flujo
   *  full-screen separado). */
  onStartUpload?: (files: FileList | File[]) => void;
  /** Si se provee, el botón "Subir nuevo producto" NO abre el file picker:
   *  navega a la pantalla explicativa (estado vacío de UploadProductPanel).
   *  Tiene prioridad sobre `onStartUpload`. */
  onStartNewProduct?: () => void;
  /** Kind del preset — controla la copy del header de upload (solo cuando
   *  `onStartUpload` está presente). */
  presetKind?: PresetV2['kind'];
  /** SOLO DESKTOP fusionado. Esconde el bloque de arriba del grid: el título "Elegí el
   *  producto para generar" + su bajada, el botón "Subir nuevo producto" y el separador
   *  "o elegí uno tuyo". Para el layout apilado del Catálogo, donde la imagen de muestra
   *  ya explica el modo arriba y el bloque repetía el mensaje ocupando media pantalla.
   *
   *  NO aplica cuando el usuario todavía no tiene productos: ahí ese bloque es la ÚNICA
   *  entrada para subir el primero, y esconderlo deja la pantalla sin salida. */
  ocultarBloqueSubida?: boolean;
  /** Si se provee, la grilla arranca con una tarjeta PLACEHOLDER "Subir nuevo producto"
   *  —mismo tamaño que las de producto, con borde punteado— y tocarla dispara esto (el
   *  host abre el drawer de alta). Es el reemplazo del bloque de subida de arriba del
   *  grid cuando se esconde con `ocultarBloqueSubida`: el alta deja de ser un bloque
   *  fijo que empuja la grilla y pasa a ser la primera opción DE la grilla. */
  onSubirProducto?: () => void;
  /**
   * Con `onSubirProducto`: en vez de UNA tarjeta de subida, DOS —"Subir un producto" y "Subir
   * varios productos"—, y ésta es la de varios (`onSubirProducto` queda para la de uno). Las dos
   * abren el pop-up de subir del modo
   * (ADR-2026-10-14-subir-productos-en-un-pop-up-desde-modelo). Sin esto, la tarjeta de
   * siempre.
   */
  onSubirVarios?: () => void;
  /** Tarjeta sin marco: el nombre deja de vivir en un pie blanco debajo de la foto y
   *  pasa a ir ENCIMA de ella, abajo, en blanco y bold sobre un degradado a negro que le
   *  da contraste. Sin borde gris — el azul de seleccionada se mantiene, que es el único
   *  que informa algo. */
  nombreSobreLaFoto?: boolean;
  /** Fija la cantidad de columnas de la grilla, ignorando el ancho medido. Sin esto son
   *  3 o 2 según el ancho del panel, que es lo que quiere el resto de la app; el Catálogo
   *  las fija para que la tarjeta tenga un tamaño concreto en su columna. Valores
   *  soportados: 2 a 5 (las clases de Tailwind están escritas a mano, ver
   *  `gridColsClass`). */
  columnasFijas?: number;
  /** Slot al PIE del panel, entre la grilla y el footer de "Confirmar". Fijo: no entra
   *  al scroll de la grilla, así los controles que van acá quedan siempre a la vista.
   *  Lo usa el rediseño de "Elegí el producto" para los ajustes rápidos (Calidad,
   *  Fondo, Formato). */
  pie?: React.ReactNode;
  /** El footer se muestra SIEMPRE (con el CTA deshabilitado mientras no haya selección)
   *  en vez de aparecer recién al elegir un producto. Con el CTA a la vista desde el
   *  principio se lee de una qué se espera y cuál es el paso siguiente; sin esto el
   *  footer entra de golpe y corre el contenido hacia arriba. */
  footerSiempreVisible?: boolean;
  /** Texto del CTA. Default "Confirmar". */
  confirmLabel?: string;
  /** Reemplaza la acción del CTA. Sin esto confirma la selección de productos; el paso de
   *  configuración lo usa para que el mismo botón dispare la generación. */
  onConfirmOverride?: () => void;
  /** Nodo a la derecha del texto del CTA — la pastilla de créditos. Reemplaza al contador
   *  de seleccionados. */
  confirmBadge?: React.ReactNode;
  /** Línea de aclaración ARRIBA del CTA, en tono bajo. La usa el `configure` a dos
   *  columnas para el resumen de la tanda ("4 imágenes · 2 modelos × 2 poses"): ese
   *  Generar es el de este panel, así que el resumen tiene que vivir acá. */
  confirmHint?: React.ReactNode;
  /**
   * Apaga el CTA aunque haya productos elegidos: lo usa el paso 2, donde falta algo de
   * la OTRA columna (sin modelo o sin pose no hay imagen que generar).
   *
   * Va como prop y no se deduce acá porque este panel no sabe nada de esa columna.
   */
  confirmDisabled?: boolean;
  /** Pistas de uso de la grilla, para el rediseño de "Elegí el producto":
   *
   *   • una línea arriba que dice que se puede elegir MÁS DE UNO (y cuántos van), y
   *   • un degradado al pie del scroll mientras quede contenido abajo.
   *
   *  Las dos hacen falta por lo mismo: la pantalla esconde el bloque de título y
   *  descripción que antes explicaba el paso, y la grilla queda cortada por una fila
   *  parcial que se puede leer como el final de la lista. */
  pistasDeUso?: boolean;
  /** Cabecera de la PANTALLA (breadcrumb + título del modo) metida adentro del panel, en
   *  su borde de arriba. La pone el host cuando no la dibuja afuera: así la fila de
   *  columnas arranca pegada al techo y se queda con el alto que antes ocupaba la
   *  cabecera. Va fija, fuera del scroll de la grilla. */
  cabecera?: React.ReactNode;
  /**
   * Modo PREVIEW: el panel se reduce a lo elegido. Se van el buscador, la tarjeta de
   * "Subir nuevo producto" y los productos NO seleccionados; quedan las miniaturas de la
   * selección, los ajustes y el CTA.
   *
   * Es para el paso de configuración, donde el panel deja de ser la herramienta con la
   * que se opera y pasa a ser el recordatorio de con qué se está generando — y le cede el
   * ancho a la columna que sí se está usando. Para cambiar la selección se vuelve al paso
   * anterior por el breadcrumb.
   */
  compacto?: boolean;
  /** Alterna entre la preview y el panel completo, sin salir del paso. Si se provee, la
   *  fila del título muestra el control. */
  onAlternarCompacto?: () => void;
  /** "Generar sin producto" (ADR-673) — solo se renderiza en el header de
   *  subida fusionado (desktop) y solo con `presetKind === 'video-influencer'`. */
  onSkipProduct?: () => void;
}

export const PickProductPanelModelo: React.FC<PickProductPanelModeloProps> = ({
  variant,
  onConfirm,
  onBack,
  tiposProductoSugeridos,
  rubro,
  initialSelectedIds,
  showPrimary = false,
  hideBack = false,
  singleSelect = false,
  maxSeleccion,
  scrollHeader = false,
  hideHeader = false,
  hideFooter = false,
  onSelectionChange,
  title = 'Elegí los productos',
  description = 'Podés elegir uno o varios.',
  onStartUpload,
  onStartNewProduct,
  onSubirVarios,
  onSkipProduct,
  presetKind,
  ocultarBloqueSubida = false,
  onSubirProducto,
  nombreSobreLaFoto = false,
  columnasFijas,
  pie,
  footerSiempreVisible = false,
  confirmLabel = 'Confirmar',
  onConfirmOverride,
  confirmBadge,
  confirmHint,
  confirmDisabled = false,
  pistasDeUso = false,
  cabecera,
  compacto = false,
  onAlternarCompacto,
}) => {
  // File picker oculto para el botón "Subir nuevo producto" (modo fusionado
  // desktop). Solo se usa cuando `onStartUpload` está presente.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadDesc =
    presetKind === 'design-auto'
      ? 'Atlas va a usar esta foto y armar la escena entera con IA — sin configurar nada más.'
      : esDesignModelo(presetKind)
        ? `Atlas va a usar esta foto y combinarla con el ${palabraDeModeloActual().singular}, la pose y el fondo que elijas.`
        : 'Atlas va a usar esta foto para aplicar el preset elegido y armar la imagen final.';

  // Columnas del grid (desktop): 3 cuando el panel queda ancho —a dos columnas, en
  // ese ancho, cada producto ocupa media pantalla (ADR-2026-08-03-anuncio-productos-tres-por-fila)—
  // y 2 cuando no. Lo decide el ANCHO, no el modo (ADR-2026-08-03-las-columnas-las-decide-el-ancho).
  //
  // Medimos el ancho REAL del contenedor scrolleable con un ResizeObserver y no el
  // del viewport: el mismo modo se ve más angosto con la sidebar abierta (en mobile
  // el ref no se monta → queda 2).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [gridCols, setGridCols] = useState(2);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 520px es el ancho a partir del cual 3 columnas dejan tarjetas de ~155px, el
    // mismo tamaño que ya tienen las de plantillas cuando pasan a 3.
    //
    // Es el DEFECTO y ya no una lista de modos —
    // ADR-2026-08-03-las-columnas-las-decide-el-ancho. La lista venía creciendo de a
    // un `kind` por vez (anuncio, después Automática y Modelo, después Catálogo) y
    // cada modo que entraba al mismo layout arrancaba con tarjetas del doble de
    // tamaño hasta que alguien se acordaba de sumarlo. Todos los que muestran la
    // preview chica a la izquierda y este panel a la derecha comparten ancho, así que
    // comparten respuesta; y donde el panel NO es ancho, el `ResizeObserver` lo mide
    // y deja 2 columnas solo.
    //
    // Plantillas es la única excepción y sigue explícita: ahí las 3 columnas son para
    // cuando el panel se vuelve MUY ancho porque la imagen de la plantilla es angosta,
    // que no es la misma pregunta.
    const minimoParaTres = presetKind === 'template' ? 768 : 520;
    const measure = () => {
      medirFinDelScroll(el);
      if (columnasFijas) {
        setGridCols(columnasFijas);
        return;
      }
      // clientWidth incluye el px-6 (48px) de padding del contenedor.
      setGridCols(el.clientWidth >= minimoParaTres ? 3 : 2);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [presetKind, columnasFijas]);
  // Mapa explícito y no `grid-cols-${n}`: Tailwind escanea el fuente, y una clase armada
  // por interpolación no llega a generarse nunca.
  const gridColsClass =
    gridCols === 5
      ? 'grid-cols-5'
      : gridCols === 4
        ? 'grid-cols-4'
        : gridCols === 3
          ? 'grid-cols-3'
          : gridCols === 1
            ? 'grid-cols-1'
            : 'grid-cols-2';
  // La lista cambia de largo al buscar, al ordenar o al volver del alta de un producto:
  // el degradado del pie tiene que apagarse si ya no quedó nada abajo.
  useEffect(() => {
    medirFinDelScroll(scrollRef.current);
  });
  // Products reales del user — vienen del ProductsContext (poblado por
  // InterfazV2 via fetchUserProducts). Si no hay productos cargados, la
  // grilla queda vacía.
  const rawProducts = useUserProducts();
  // Productos usados en la última generación — llevan un badge "Último
  // utilizado". Leemos los ids una sola vez al montar. Ya NO los flotamos
  // arriba: para eso está el orden explícito "Últimos utilizados" del toolbar
  // (que además cubre TODO el historial, no solo la última generación), y
  // flotarlos hacía que "Últimos subidos" no respetara su propio criterio.
  const lastUsedIds = useMemo(() => getLastUsedProductIds(), []);
  const lastUsedSet = useMemo(() => new Set(lastUsedIds), [lastUsedIds]);
  // Buscador + orden del catálogo. Con búsqueda activa la grilla se aplana
  // (sin secciones Sugeridos / Otros).
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<ProductCatalogSort>(DEFAULT_PRODUCT_SORT);

  // ── Mobile: header colapsable en vez de toolbar sticky ──────────────
  // En mobile el toolbar NO va adentro del contenedor scrolleable: ahí el
  // sticky quedaba anclado unos px por debajo del borde y se veían pasar los
  // bordes de las cards por esa franja (dos intentos de encontrar el offset no
  // dieron con la causa). Va como fila fija, y para que igual "desaparezca" el
  // bloque de título al scrollear —que era el pedido— ese bloque se colapsa por
  // altura. Resultado: el orden visual de siempre (título arriba, buscador
  // debajo) con una barra que no se mueve nunca, sin depender de
  // `position: sticky`.
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  /**
   * ¿El scroll de la grilla tiene contenido por debajo de lo que se ve?
   *
   * Decide si se dibuja el degradado del pie. Se recalcula al scrollear y cuando cambia
   * el contenido o el tamaño del contenedor — si sólo se mirara el scroll, arrancaría
   * apagado hasta el primer movimiento, que es justo cuando hace falta.
   */
  const [quedaContenidoAbajo, setQuedaContenidoAbajo] = useState(false);
  const medirFinDelScroll = (el: HTMLDivElement | null) => {
    if (!el) return;
    // 4px de tolerancia: con zoom del browser `scrollTop` queda fraccionario y la resta
    // nunca da exactamente 0 al final.
    setQuedaContenidoAbajo(
      el.scrollHeight - el.scrollTop - el.clientHeight > 4,
    );
  };

  const handleCollapseOnScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop;
    // Umbrales distintos para colapsar y expandir: sin esa histéresis el header
    // titila cuando el scroll queda justo en el límite.
    setHeaderCollapsed((prev) => (prev ? top > 8 : top > 24));
  };
  const collapsibleClass = (open: boolean) =>
    `shrink-0 overflow-hidden transition-all duration-200 ease-out ${
      open ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
    }`;
  // Con una búsqueda, también por los datos del producto (ADR-2026-10-21-la-busqueda-mira-los-datos-del-producto).
  const indiceDeBusqueda = useIndiceDeBusqueda(
    rawProducts.map((p) => p.id),
    query.trim().length > 0,
  );
  const { items: products, isSearching, noResults } = useMemo(
    () => applyProductCatalogFilter(rawProducts, { query, sort, indice: indiceDeBusqueda }),
    [rawProducts, query, sort, indiceDeBusqueda],
  );
  const hasProducts = rawProducts.length > 0;
  // El toolbar solo aparece con un catálogo lo bastante grande: con pocos
  // productos entra todo en pantalla y el buscador es ruido.
  const showToolbar =
    !compacto && rawProducts.length >= MIN_PRODUCTS_FOR_TOOLBAR;
  const productsLoaded = useUserProductsLoaded();
  // Mientras el catálogo carga (sin cache aún) mostramos skeleton en vez de
  // un grid vacío o el CTA de "subir" — todavía no sabemos si tiene productos.
  const loadingProducts = !productsLoaded && !hasProducts;
  // Modo fusionado (desktop) SIN productos: lo único lógico es subir uno, así
  // que mostramos SOLO el CTA de upload — sin grid vacío, sin separador "o
  // elegí uno tuyo" y sin botón Confirmar, centrado para que se vea limpio.
  // Solo una vez que el catálogo cargó (sino durante la carga mostraría el
  // CTA centrado en lugar del skeleton).
  const mergedEmptyUpload = !!onStartUpload && !hasProducts && productsLoaded;
  // Multi-selección por toggle. Mantenemos el ORDEN: el primer elemento es
  // el "principal". Al quitar el principal, el siguiente pasa a serlo; al
  // vaciar todo, el próximo en seleccionarse será el nuevo principal.
  const [selectedIds, setSelectedIds] = useState<string[]>(
    () => initialSelectedIds ?? [],
  );

  // ── Editar producto (lápiz por card) ────────────────────────────────
  // Producto en edición → monta la modal "Editar producto" (EditProductFlow →
  // VariantForm), que ya resuelve el responsive (panel lateral desktop /
  // bottom-sheet mobile) y portalea a body. `companyId` + `refreshProducts`
  // vienen del ProductEditContext (provisto por InterfazV2) para no threadear
  // props por todos los call-sites del picker.
  const [editingProduct, setEditingProduct] = useState<MockProductV2 | null>(
    null,
  );
  const { companyId, refreshProducts } = useProductEdit();
  // Si un producto seleccionado desaparece del catálogo (p.ej. se borró desde
  // la modal), lo sacamos de la selección para no dejar un id fantasma en el
  // contador ni en el confirm.
  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => rawProducts.some((p) => p.id === id)),
    );
  }, [rawProducts]);

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      // Selección única (video-influencer): tocar otro producto REEMPLAZA al
      // elegido; tocar el mismo lo deselecciona (ADR-563).
      if (singleSelect) return prev.includes(id) ? [] : [id];
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      // En el tope, sumar no hace nada: se quita una y recién ahí entra otra. Devolver
      // `prev` tal cual —y no una copia— evita el re-render y el `onSelectionChange` de
      // una selección que no cambió.
      if (maxSeleccion != null && prev.length >= maxSeleccion) return prev;
      return [...prev, id];
    });
  };
  const handleConfirm = () => {
    if (selectedIds.length === 0) return;
    // Devolvemos en el ORDEN de selección (no el orden del catálogo) para
    // preservar cuál es el principal (primer elemento). El mapa se arma sobre
    // `rawProducts`, NO sobre la lista filtrada: si el user selecciona algo y
    // después busca otra cosa, lo ya elegido sigue seleccionado aunque no esté
    // visible — armarlo con `products` lo dejaría afuera del confirm.
    const byId = new Map(rawProducts.map((p) => [p.id, p]));
    onConfirm(
      selectedIds.map((id) => byId.get(id)).filter(Boolean) as MockProductV2[],
    );
  };
  const hasSelection = selectedIds.length > 0;

  // Reporte de selección EN VIVO para quien oculta el footer y confirma por su
  // cuenta. Se mapea sobre `rawProducts` (no la lista filtrada) por lo mismo
  // que `handleConfirm`: lo ya elegido sigue contando aunque el buscador lo
  // esté ocultando.
  useEffect(() => {
    if (!onSelectionChange) return;
    const byId = new Map(rawProducts.map((p) => [p.id, p]));
    onSelectionChange(
      selectedIds.map((id) => byId.get(id)).filter(Boolean) as MockProductV2[],
    );
    // `onSelectionChange` queda fuera de las deps a propósito: los callers la
    // pasan inline (identidad nueva en cada render) y meterla dispara un loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, rawProducts]);

  // ── Sugerencias por tipo de producto ────────────────────────────────
  // Solo para rubros de moda — sería ruidoso aplicarlo a otros rubros
  // donde model_view casi no se completa. Si el template no tiene tipos
  // sugeridos o no hay matches, mostramos un único grid sin sección.
  const SUGGEST_RUBROS = new Set(['Moda e Indumentaria']);
  const shouldSuggest =
    !!rubro &&
    SUGGEST_RUBROS.has(rubro) &&
    Array.isArray(tiposProductoSugeridos) &&
    tiposProductoSugeridos.length > 0;
  const suggestedSet = shouldSuggest
    ? new Set(
        (tiposProductoSugeridos as string[]).map((s) =>
          s.trim().toLowerCase(),
        ),
      )
    : null;
  const { suggested, others } = (() => {
    // Buscando → una sola lista de resultados: partirla en "Sugeridos" y
    // "Otros" estorba cuando lo que querés es un producto puntual.
    if (!suggestedSet || isSearching) {
      return { suggested: [] as MockProductV2[], others: products };
    }
    const sug: MockProductV2[] = [];
    const oth: MockProductV2[] = [];
    for (const p of products) {
      const mv = (p.modelView || '').trim().toLowerCase();
      if (mv && suggestedSet.has(mv)) {
        sug.push(p);
      } else {
        oth.push(p);
      }
    }
    return { suggested: sug, others: oth };
  })();
  const hasSuggestions = suggested.length > 0;

  // Tile reutilizado en ambos viewports. Rounded varia (xl mobile · 2xl
  // desktop) para mantener el look existente.
  /**
   * Tarjeta PLACEHOLDER de "Subir nuevo producto" — primera de la grilla.
   *
   * Espeja la geometría de `renderTile` (foto cuadrada, borde de 2px y el mismo pie —o
   * la falta de pie, con `nombreSobreLaFoto`) para que caiga en la cuadrícula sin
   * desalinear la fila: si midiera distinto, estiraría toda su fila.
   */
  // Las dos formas de subir —"Un producto" y "Subir varios productos"— en una fila PROPIA,
  // arriba de la grilla y repartiéndose el ancho, en vez de comerse dos celdas de la
  // primera fila (ADR-2026-10-14-tarjetas-de-subida-a-todo-el-ancho). Con tres o más
  // columnas, apaisadas: la fila mide lo mismo que una de productos.
  const dosFormasDeSubir = !!(onSubirVarios && onSubirProducto);
  const filaDeSubida = dosFormasDeSubir ? (
    <div className="grid grid-cols-2 gap-3 pb-3">
      {(['uno', 'varios'] as const).map((variante) => (
        <TarjetaDeSubidaEnGrilla
          key={variante}
          variante={variante}
          onClick={variante === 'uno' ? onSubirProducto! : onSubirVarios!}
          aspecto={gridCols >= 3 ? 'aspect-[3/2]' : 'aspect-square'}
          redondeo="rounded-2xl"
        />
      ))}
    </div>
  ) : null;

  const renderTileSubir = (radius: 'xl' | '2xl') => {
    const rounded = radius === 'xl' ? 'rounded-xl' : 'rounded-2xl';
    // Con las dos formas de subir, las tarjetas van en su propia fila (`filaDeSubida`),
    // no como celda de la grilla.
    if (dosFormasDeSubir) return null;
    // La tarjeta de subir de Mi marca: punteado celeste, degradé y el abanico de fotos
    // (`AbanicoDeFotos`), que se abre en hover —
    // ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca. El borde sigue
    // siendo de 2px para medir lo mismo que las tarjetas de producto.
    return (
      <button
        key="__subir-producto__"
        type="button"
        onClick={onSubirProducto}
        className={`group relative text-left ${rounded} overflow-hidden border-2 border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] transition-[border-color,box-shadow,transform] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary`}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
        />
        {/* Con el nombre sobre la foto las tarjetas de producto pierden el pie y quedan
            cuadradas: acá el texto también entra al cuadrado, sino esta tarjeta sería
            más alta que las otras y estiraría la primera fila (y con ella el recorte de
            una fila y media, que se calcula sobre el alto de fila). */}
        {nombreSobreLaFoto ? (
          <div className="relative aspect-square flex flex-col items-center justify-center gap-3 px-2">
            <AbanicoDeFotos />
            <p className="text-[12.5px] font-semibold text-[#16181D] text-center leading-tight">
              Subir nuevo producto
            </p>
          </div>
        ) : (
          <>
            <div className="relative aspect-square flex items-center justify-center">
              <AbanicoDeFotos />
            </div>
            <div className="relative px-3 py-2.5">
              <p className="text-[13.5px] font-semibold text-[#16181D] truncate">
                Subir nuevo producto
              </p>
            </div>
          </>
        )}
      </button>
    );
  };

  const renderTile = (p: MockProductV2, radius: 'xl' | '2xl') => {
    const isSel = selectedIds.includes(p.id);
    const isPrimary = showPrimary && isSel && selectedIds[0] === p.id;
    const isLastUsed = lastUsedSet.has(p.id);
    // Con el tope alcanzado, las que no están elegidas se ven pero no se tocan — el
    // mismo criterio que el CTA apagado del pie: sigue estando, dice que no todavía.
    const topeAlcanzado =
      !isSel && maxSeleccion != null && selectedIds.length >= maxSeleccion;
    const rounded = radius === 'xl' ? 'rounded-xl' : 'rounded-2xl';
    return (
      // Card = div con role=button (NO <button>): así el lápiz de editar puede
      // ser un <button> real adentro sin anidar button-en-button (HTML inválido).
      <div
        key={p.id}
        role="button"
        tabIndex={topeAlcanzado ? -1 : 0}
        aria-disabled={topeAlcanzado || undefined}
        onClick={() => {
          if (!topeAlcanzado) toggle(p.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!topeAlcanzado) toggle(p.id);
          }
        }}
        className={`text-left ${rounded} overflow-hidden bg-white ${
          topeAlcanzado ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
        } ${
          // 1.5px en las tarjetas del rediseño y 2px en las clásicas. El grosor va junto
          // con el color de abajo: los dos estados —gris al 10% y azul de seleccionada—
          // tienen que medir lo MISMO, sino la tarjeta cambia de tamaño al elegirla.
          nombreSobreLaFoto ? 'border-[1.5px]' : 'border-2'
        } ${
          isSel
            ? 'border-brand-primary'
            : nombreSobreLaFoto
              ? // El borde vive ACÁ y no en la foto de adentro: la tarjeta es la que
                // tiene el `rounded` y el `overflow-hidden`, así que un borde en el hijo
                // cuadrado se ve CORTADO en las cuatro esquinas —la curva lo clipea—.
                // Acá sigue la curva y cierra.
                //
                // Ocupa el mismo lugar que el azul de seleccionada, así que la tarjeta
                // no cambia de tamaño al elegirla.
                'border-black/10 hover:border-brand-primary/40'
              : radius === '2xl'
                ? 'border-neutral-200 hover:border-brand-primary/40'
                : 'border-neutral-200'
        } active:scale-[0.98] transition-all relative`}
      >
        <div className="relative aspect-square bg-neutral-100 overflow-hidden">
          {/* `object-cover` = el modo "Fill" de Figma: la foto se escala hasta tapar la
              caja entera y lo que sobra de un lado se recorta. Sin barras y sin deformar,
              que es lo que se pidió; el precio es el recorte —una foto vertical pierde
              arriba y abajo, una apaisada los costados—.

              Se probó la variante "foto entera + copia borrosa de fondo" (object-contain
              sobre la misma imagen ampliada y desenfocada): mostraba el producto completo,
              pero la foto quedaba flotando más chica y el fondo se leía como si la imagen
              estuviera cortada a los costados. Volvió a Fill a pedido. */}
          <CachedImg
            src={p.imageUrl}
            alt={p.name}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {isLastUsed && (
            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-brand-black text-white text-[10px] font-semibold shadow">
              Último utilizado
            </span>
          )}
          {isSel ? (
            <div className="absolute top-2 right-2 flex items-center gap-1.5">
              {isPrimary && (
                <span className="px-2 py-0.5 rounded-full bg-brand-primary text-white text-[10.5px] font-semibold shadow">
                  Principal
                </span>
              )}
              {/* 20px, el mismo tamaño que el radio vacío de la tarjeta sin elegir: si
                  midieran distinto, el control saltaría de tamaño al seleccionar. */}
              <div className="w-5 h-5 rounded-full bg-brand-primary flex items-center justify-center shadow">
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              </div>
            </div>
          ) : (
            // Radio VACÍO en las no elegidas: es lo que dice, sin tocar nada, que cada
            // tarjeta se prende y se apaga por su cuenta. Con el check apareciendo sólo
            // al seleccionar, la grilla se leía como "elegí una" hasta que probabas.
            //
            // Va en `singleSelect` no: ahí elegir una APAGA la anterior, y una fila de
            // radios vacíos prometería lo contrario.
            //
            // El borde blanco + el fondo negro translúcido lo sostienen sobre cualquier
            // foto: sin eso desaparece contra un packshot en fondo blanco.
            nombreSobreLaFoto &&
            !singleSelect && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full border-[1.5px] border-white/90 bg-black/15 shadow-sm" />
            )
          )}
          {/* Nombre SOBRE la foto. El degradado arranca transparente arriba y llega a
              negro abajo: es lo que hace legible el texto blanco sobre una foto de
              contenido impredecible (un packshot en fondo blanco lo dejaría invisible).
              `pt-8` para que el degradado tenga recorrido y no corte de golpe. */}
          {/* Sin el lápiz de editar: en esta tarjeta el nombre va sobre la foto, y el
              botón quedaba encima de la imagen tapándola y compitiendo con el tap de
              seleccionar, que es lo único que se hace acá. Editar el producto sigue
              estando en la tarjeta clásica (`nombreSobreLaFoto` apagado) y en Mi marca. */}
          {nombreSobreLaFoto && (
            <div className="absolute inset-x-0 bottom-0 px-2.5 pt-8 pb-2 bg-gradient-to-t from-black/85 via-black/45 to-transparent">
              <p className="text-[11.5px] font-bold text-white truncate">
                {p.name}
              </p>
              <SkuDelProducto sku={p.sku} chico sobreFoto />
            </div>
          )}
        </div>
        <div className={nombreSobreLaFoto ? 'hidden' : 'px-3 py-2.5'}>
          {/* Nombre + lápiz de editar en la MISMA fila (estilo Estudio Virtual):
              lápiz plano gris a la derecha. Visible solo cuando la card está
              seleccionada; si no, transparente + sin eventos (pero reserva su
              lugar, así el nombre no se corre al seleccionar). stopPropagation
              para no togglear la selección al tocarlo. */}
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[13.5px] font-medium text-brand-black/90 truncate">
                {p.name}
              </p>
              <SkuDelProducto sku={p.sku} />
            </div>
            <button
              type="button"
              tabIndex={isSel ? 0 : -1}
              title="Editar producto"
              aria-label="Editar producto"
              onClick={(e) => {
                e.stopPropagation();
                setEditingProduct(p);
              }}
              className={`p-1.5 rounded-md transition-colors shrink-0 ${
                isSel
                  ? 'text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100'
                  : 'text-transparent pointer-events-none'
              }`}
            >
              <Pencil className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Modal "Editar producto" — reusa EditProductFlow (mismo que Mi Marca /
  // Estudio Virtual): VariantForm portalea a body (z-[60]+), así que queda por
  // encima del sheet del picker sin recortarse. onSaved refresca el catálogo del
  // context; onClose cierra. Requiere companyId (del ProductEditContext).
  const editModal =
    editingProduct && companyId ? (
      <EditProductFlow
        companyId={companyId}
        product={{ id: editingProduct.id, name: editingProduct.name }}
        onClose={() => setEditingProduct(null)}
        onSaved={refreshProducts}
      />
    ) : null;

  if (variant === 'mobile') {
    return (
      <>
        {/* Drag handle — pill gris centrada arriba. */}
        <div className="py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* iOS header: Cancelar (azul, izq) · título centrado · Confirmar (der). */}
        <div className="flex items-center justify-between px-4 pb-3 shrink-0">
          <button
            type="button"
            onClick={onBack}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity w-20 text-left"
          >
            Cancelar
          </button>
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Mis productos
          </h3>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={selectedIds.length === 0}
            className="text-[15px] text-brand-primary font-semibold disabled:opacity-40 active:opacity-50 transition-opacity w-20 text-right"
          >
            {selectedIds.length > 0
              ? `Listo (${selectedIds.length})`
              : 'Listo'}
          </button>
        </div>
        {/* Subtítulo colapsable + buscador debajo, ambos AFUERA del scroll —
            mismo patrón que la pantalla full-screen de mobile (ver el comentario
            de `headerCollapsed`). */}
        <div
          className={`px-4 ${collapsibleClass(
            !(showToolbar && !loadingProducts && headerCollapsed),
          )}`}
        >
          <p className="text-[12.5px] text-[#3C3C43]/55 pb-4">{description}</p>
        </div>
        {showToolbar && !loadingProducts && (
          <ProductCatalogToolbar
            query={query}
            onQueryChange={setQuery}
            sort={sort}
            onSortChange={setSort}
            className="shrink-0 px-4 pt-1"
            inputId="v2-pick-product-search-mobile"
          />
        )}
        <div
          onScroll={handleCollapseOnScroll}
          className="flex-1 overflow-y-auto px-4 pb-6"
        >
          {loadingProducts ? (
            <ProductGridSkeleton
              count={6}
              gridClassName="grid grid-cols-2 gap-3"
              tileClassName="rounded-xl"
              withLabel
            />
          ) : noResults ? (
            <ProductCatalogNoResults query={query} onClear={() => setQuery('')} />
          ) : hasSuggestions ? (
            <>
              <div className="flex items-center gap-1.5 mb-2 mt-1">
                <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/55" />
                <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55">
                  Sugeridos para esta plantilla
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {suggested.map((p) => renderTile(p, 'xl'))}
              </div>
              {others.length > 0 && (
                <>
                  <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55 mt-5 mb-2">
                    Otros productos
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {others.map((p) => renderTile(p, 'xl'))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {products.map((p) => renderTile(p, 'xl'))}
            </div>
          )}
        </div>
        {editModal}
      </>
    );
  }

  // Desktop variant — layout self-contained:
  //   • Header (Volver + título + subtítulo) — shrink-0, o dentro del scroll
  //     si `scrollHeader` (pantalla full-screen de mobile)
  //   • Grilla scrolleable — flex-1 overflow-y-auto
  //   • Footer con Confirmar — shrink-0, border-top
  // Importante: el aside padre NO debe envolver esto en otro overflow-y
  // (ver la rama `step === 'pick-product'` del PresetDetailSheet desktop).

  // Header "Elegí los productos" — solo cuando NO está fusionado con el upload
  // (ej. plantillas). En modo fusionado (modos de diseño desktop) el header
  // "Elegí el producto" + botón Subir van DENTRO del scroll, abajo.
  // El padding horizontal lo pone el contenedor cuando va adentro del scroll,
  // que ya tiene `px-6`.
  // Buscador + orden. En mobile se renderiza afuera del scroll (ver el bloque
  // de `headerCollapsed`); en desktop va adentro, sticky, debajo del bloque de
  // subida.
  const toolbar =
    showToolbar && !loadingProducts && !mergedEmptyUpload ? (
      <ProductCatalogToolbar
        query={query}
        onQueryChange={setQuery}
        sort={sort}
        onSortChange={setSort}
        // Fuera del scroll pone su propio padding horizontal; adentro compensa
        // el `px-6` del contenedor para que el fondo blanco llegue a los bordes.
        className={scrollHeader ? 'shrink-0 px-6 pt-4' : '-mx-6 px-6 pt-4'}
        inputId="v2-pick-product-search-desktop"
      />
    ) : null;

  // `hideHeader` anula el bloque entero (título + descripción + Volver). Los
  // dos sitios de render de abajo ya están guardados por `headerInner &&`, así
  // que con devolver null alcanza y no queda padding fantasma.
  const headerInner = hideHeader ? null : !onStartUpload ? (
    <div className="pt-6 pb-5">
      {!hideBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-brand-primary text-[13px] mb-3 hover:opacity-75 transition-opacity"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Volver
        </button>
      )}
      <p className="text-[16px] font-semibold text-brand-black/90">{title}</p>
      <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5">{description}</p>
    </div>
  ) : null;

  // La fila del recuento ("4 productos" / "2 productos elegidos — tocá otros…").
  // Comprimido es también donde vive el control de abrir el panel, así que hay que
  // saber si se va a renderizar ANTES de decidir dónde poner ese control.
  /**
   * Título con la grilla todavía sin tocar.
   *
   * En Catálogo se dice explícitamente qué pasa con varios elegidos: el modo genera el
   * catálogo COMPLETO de cada producto (vistas × producto), no una foto que los combine.
   * Es la confusión natural con un modo que acepta multi-selección —en "Automática", por
   * ejemplo, varios productos sí salen juntos en una imagen— y es la diferencia entre
   * elegir tres productos esperando tres catálogos o esperando una foto de outfit.
   */
  /**
   * v2: la fila del recuento se queda SIN TEXTO — ver
   * ADR-2026-08-21-modelo-v2-cabecera-sin-breadcrumb.
   *
   * Decía "Elegí uno o varios productos" con la grilla sin tocar y "N productos elegidos
   * — tocá otros para sumarlos" con algo elegido, siempre justo debajo del título de la
   * pantalla: una segunda línea de encabezado en la columna que le pelea el alto a la
   * grilla, diciendo lo que la grilla de abajo ya muestra.
   *
   * Lo que NO se va con el texto son los controles que compartían la fila: el "Listo"
   * (panel abierto) y el ícono de abrir (panel comprimido). Por eso la fila sobrevive
   * cuando tiene uno que alojar —`onAlternarCompacto`— y desaparece cuando no, en vez
   * de quedar como una banda de padding vacío.
   */
  const hayFilaControles =
    pistasDeUso &&
    !loadingProducts &&
    !mergedEmptyUpload &&
    !!onAlternarCompacto &&
    // En `singleSelect` la fila no va… salvo COMPRIMIDA, que es el único lugar donde
    // entra el control de abrir el panel sin gastar una fila propia
    // (ADR-2026-08-21-video-igual-que-modelo).
    (!singleSelect || compacto);

  // Control de abrir el panel comprimido. Va como ícono y no como texto: el panel
  // mide 280px y una etiqueta al lado del recuento le comería media línea.
  const botonAbrir = onAlternarCompacto ? (
    <button
      type="button"
      onClick={onAlternarCompacto}
      aria-label="Abrir el panel de productos"
      title="Abrir el panel de productos"
      className="shrink-0 grid h-8 w-8 place-items-center rounded-lg text-brand-black/40 hover:bg-black/[0.04] hover:text-brand-black/70 transition-colors"
    >
      <PanelLeftOpen className="w-[18px] h-[18px]" />
    </button>
  ) : null;

  return (
    <div className="flex flex-col h-full">
      {cabecera && <div className="shrink-0">{cabecera}</div>}
      {/* Comprimido el control va EN LA FILA del recuento, a su derecha: en una
          columna de 280px una fila propia para un ícono de 32px costaba ~50px de alto
          que salen de las miniaturas. Sólo cuando esa fila no se renderiza (sin
          productos, cargando, `singleSelect`) el control se queda solo arriba, que es
          el único lugar que queda. */}
      {compacto && !hayFilaControles && botonAbrir && (
        <div className="shrink-0 px-6 pt-4">{botonAbrir}</div>
      )}
      {/* Desktop: header fijo. Mobile (`scrollHeader`): header colapsable +
          buscador debajo, ambos afuera del scroll. Si no hay toolbar no tiene
          sentido colapsar nada — no se gana una barra visible a cambio. */}
      {!scrollHeader && headerInner && (
        <div className="shrink-0 px-6">{headerInner}</div>
      )}
      {scrollHeader && headerInner && (
        <div className={`px-6 ${collapsibleClass(!(toolbar && headerCollapsed))}`}>
          {headerInner}
        </div>
      )}
      {scrollHeader && toolbar}

      {/* Que se puede elegir MÁS DE UNO no se ve en ningún lado hasta que elegís el
          segundo: las tarjetas no tienen checkbox y el bloque de título que lo explicaba
          está escondido en esta pantalla. Con una selección encima pasa a contar cuántos
          van, que es la otra pregunta del momento. No aplica con `singleSelect`, donde
          elegir un segundo reemplaza al primero. */}
      {hayFilaControles && (
        // Sin el texto, la fila es sólo su control: va contra el borde derecho
        // (`justify-end`) y sin el `items-baseline` que existía para alinearlo con una
        // línea de texto que ya no está.
        <div
          className={`shrink-0 px-6 flex justify-end items-center ${
            compacto ? 'pt-4 pb-4' : 'pt-3 pb-1'
          }`}
        >
        {/* Abierto va con texto: ahí el panel es ancho y "Listo" dice qué pasa al
            tocarlo mejor que cualquier ícono. Comprimido, el mismo lugar lo ocupa el
            ícono de abrir. */}
        {compacto ? (
          botonAbrir
        ) : (
          <button
            type="button"
            onClick={onAlternarCompacto}
            className="shrink-0 inline-flex items-center gap-0.5 text-brand-primary text-[13px] font-medium hover:opacity-75 transition-opacity"
          >
            Listo
            <Check className="w-3.5 h-3.5" />
          </button>
        )}
        </div>
      )}

      {/* Scrollable: (header de upload + botón Subir, modo fusionado) + grilla */}
      {/* Sin `padding-top`: el toolbar de abajo es sticky y un padding acá lo
          ancla por debajo, dejando una franja donde se ven pasar las cards. El
          espacio de arriba lo pone el bloque de subida (`pt-6`) o el propio
          toolbar, que al ser blanco tapa. */}
      <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={scrollRef}
        onScroll={(e) => {
          if (scrollHeader) handleCollapseOnScroll(e);
          if (pistasDeUso) medirFinDelScroll(e.currentTarget);
        }}
        className={`flex-1 min-h-0 overflow-y-auto px-6${mergedEmptyUpload ? ' flex flex-col justify-center' : ''}`}
      >
        {/* Cuando hay toolbar, el aire que lo separa del bloque de subida ya lo
            pone su propio `pt-4` (que va adentro de la caja blanca del sticky):
            si además dejáramos el `mb-5`, al no estar scrolleado el buscador
            quedaba flotando lejos del separador "o elegí uno tuyo". */}
        {onStartUpload && (!ocultarBloqueSubida || mergedEmptyUpload) && (
          <div
            className={
              mergedEmptyUpload ? 'pt-6' : showToolbar ? 'pt-6 mb-1' : 'pt-6 mb-5'
            }
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  onStartUpload(e.target.files);
                  e.target.value = '';
                }
              }}
            />
            <div className="mb-4">
              <p className="text-[17px] font-semibold leading-tight text-[#16181D]">
                Elegí el producto para generar
              </p>
              <p className="mt-1 text-[13.5px] leading-snug text-black/45">
                {uploadDesc}
              </p>
            </div>
            {/* Con las dos tarjetas de subir ("Subir un producto" / "Subir varios
                productos", `filaDeSubida`) esta tarjeta grande sobra: sin productos se
                dibujaban las dos formas, una pegada a la otra
                (ADR-2026-10-29-una-sola-forma-de-subir-sin-productos). */}
            {!dosFormasDeSubir && (
            <>
              {/* La tarjeta de subir de Mi marca, con el abanico —
                  ADR-2026-10-06-subir-producto-en-los-modos-con-la-cara-de-mi-marca. Con un
                  producto ya elegido se achica: deja de ser el paso principal. */}
              <button
                type="button"
                onClick={onStartNewProduct ?? (() => fileInputRef.current?.click())}
                className={`group relative flex w-full items-center overflow-hidden rounded-3xl border-[1.5px] border-dashed border-[rgb(var(--brand-primary-rgb)_/_0.215)] bg-gradient-to-b from-white to-[rgb(var(--brand-primary-rgb)_/_0.055)] text-center transition-[border-color,box-shadow,padding] duration-300 hover:border-brand-primary hover:shadow-[0_12px_32px_-14px_rgb(var(--brand-primary-rgb)_/_0.45)] active:scale-[0.99] ${
                  hasSelection
                    ? 'flex-row justify-center gap-3 px-4 py-3'
                    : 'flex-col justify-center gap-4 px-5 py-7'
                }`}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 top-0 h-2/3 bg-[radial-gradient(60%_70%_at_50%_0%,rgb(var(--brand-primary-rgb)_/_0.10),transparent)] opacity-70 transition-opacity duration-300 group-hover:opacity-100"
                />
                {hasSelection ? (
                  <span className="relative flex h-8 w-8 items-center justify-center rounded-full bg-brand-primary text-white shadow-[0_4px_10px_-2px_rgb(var(--brand-primary-rgb)_/_0.55)]">
                    <Plus className="h-4 w-4" strokeWidth={2.75} />
                  </span>
                ) : (
                  <AbanicoDeFotos />
                )}
                <span className="relative">
                  <span className="block text-[15px] font-semibold leading-tight text-[#16181D]">
                    Subir nuevo producto
                  </span>
                  {!hasSelection && (
                    <span className="mt-1 block text-[13px] leading-snug text-black/45">
                      Cargá sus fotos y empezá a crear
                    </span>
                  )}
                </span>
              </button>
            </>
            )}
            {/* "Sin producto" (ADR-673) — SOLO el video con influencer. Va
                pegado abajo del botón de subir, con menos peso visual: es la
                salida alternativa, no el camino principal. */}
            {onSkipProduct && presetKind === 'video-influencer' && (
              <button
                type="button"
                onClick={onSkipProduct}
                className="w-full mt-2 inline-flex items-center justify-center gap-2.5 py-3 px-6 rounded-2xl text-[14.5px] font-medium text-[#3C3C43]/70 hover:text-brand-black hover:bg-black/[0.03] transition-colors"
              >
                <PackageX className="w-[18px] h-[18px]" />
                Generar sin producto
              </button>
            )}
            {/* Con `filaDeSubida`, las tarjetas de subir ya separan el bloque de la grilla. */}
            {hasProducts && !dosFormasDeSubir && (
              <div className="flex items-center gap-3 mt-5 mb-1">
                <div className="flex-1 h-px bg-black/[0.06]" />
                <span className="text-[12px] text-[#3C3C43]/45 shrink-0">
                  o elegí uno tuyo
                </span>
                <div className="flex-1 h-px bg-black/[0.06]" />
              </div>
            )}
          </div>
        )}
        {/* Toolbar arriba de la grilla (desktop) — en el modo fusionado queda
            debajo del separador "o elegí uno tuyo", que es donde empieza el
            catálogo. Con `scrollHeader` ya se renderizó afuera del scroll. */}
        {!scrollHeader && toolbar}
        {loadingProducts ? (
          <ProductGridSkeleton
            count={6}
            gridClassName={`grid ${gridColsClass} gap-3 pb-3`}
            tileClassName="rounded-2xl"
            withLabel
          />
        ) : noResults ? (
          <ProductCatalogNoResults query={query} onClear={() => setQuery('')} />
        ) : hasSuggestions ? (
          <div className="pb-3">
            {filaDeSubida}
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#3C3C43]/55" />
              <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55">
                Sugeridos para esta plantilla
              </p>
            </div>
            <div className={`grid ${gridColsClass} gap-3`}>
              {onSubirProducto && renderTileSubir('2xl')}
              {suggested.map((p) => renderTile(p, '2xl'))}
            </div>
            {others.length > 0 && (
              <>
                <p className="text-[11.5px] uppercase tracking-wide font-semibold text-[#3C3C43]/55 mt-5 mb-2">
                  Otros productos
                </p>
                <div className={`grid ${gridColsClass} gap-3`}>
                  {others.map((p) => renderTile(p, '2xl'))}
                </div>
              </>
            )}
          </div>
        ) : (
          <>
          {!compacto && filaDeSubida}
          <div className={`grid ${gridColsClass} gap-3 pb-3`}>
            {onSubirProducto && !compacto && renderTileSubir('2xl')}
            {(compacto
              ? products.filter((p) => selectedIds.includes(p.id))
              : products
            ).map((p) => renderTile(p, '2xl'))}
          </div>
          </>
        )}
      </div>
      {/* Degradado del pie: la última fila se desvanece en vez de cortarse contra el
          borde, que es lo que hace leer la grilla como terminada. Se apaga al llegar
          abajo — si quedara siempre, dejaría de significar "hay más". */}
      {pistasDeUso && quedaContenidoAbajo && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-white via-white/85 to-transparent" />
      )}
      </div>

      {/* Ajustes al pie. Van FUERA del área scrolleable —y antes del footer— para que
          no haya que scrollear la grilla hasta el final para encontrarlos. En el estado
          vacío no se muestran: ahí todavía no hay nada que configurar. */}
      {pie && !mergedEmptyUpload && <div className="shrink-0">{pie}</div>}

      {/* Footer con Confirmar. Se oculta en el modo fusionado SIN productos:
          ahí el único paso lógico es subir, no hay nada que confirmar. En el
          modo fusionado CON productos el footer aparece (con un slide-up sutil)
          recién cuando hay una selección — antes el CTA es subir/elegir. */}
      {!hideFooter &&
        !mergedEmptyUpload &&
        (footerSiempreVisible || !onStartUpload || hasSelection) && (
          <div
            className={`shrink-0 px-6 pt-3 pb-4 border-t border-black/[0.05] bg-white${
              // La animación de entrada es sólo para el footer que APARECE. Con
              // `footerSiempreVisible` no aparece nunca —está desde el principio— y el
              // slide-up lo dispararía cada vez que re-renderiza.
              onStartUpload && !footerSiempreVisible ? ' animate-mode-swap' : ''
            }`}
          >
            {confirmHint && (
              <p className="mb-2 text-center text-[12.5px] leading-snug text-[#3C3C43]/55">
                {confirmHint}
              </p>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onConfirmOverride ?? handleConfirm}
                disabled={selectedIds.length === 0 || confirmDisabled}
                className="flex-1 min-w-0 inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
              >
                <span className="truncate">{confirmLabel}</span>
                {confirmBadge}
                {/* Sin la pastilla con la cantidad cuando el título ya la dice ("3
                    productos elegidos"): eran dos lugares informando lo mismo a diez
                    centímetros uno del otro. Donde no hay título —el resto de los
                    modos— la pastilla sigue siendo la única señal y se mantiene. */}
                {!confirmBadge && !pistasDeUso && selectedIds.length > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white/25 text-[12px] font-bold leading-none">
                    {selectedIds.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        )}
      {editModal}
    </div>
  );
};
